import { injectIntl } from "react-intl";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import OffersTable from "./OffersTable";
import NewOffer from "./NewOffer";
import OfferDelete from "./OfferDelete";
import CreativeDelete from "./CreativeDelete";
import apiService from "src/services/api.service";
import Empty from "src/components/Empty";
import { PageEnum, OperationType } from "src/helpers/Enums";
import SearchLine from "src/components/SearchLine";

const OFFERS_LIST_SORT_FIELDS = [
  "creation_date",
  "name",
  "totalRevenue",
  "score",
  "emailScore",
  "smsScore",
];

const parseOffersListPage = (sp) =>
  Math.max(1, parseInt(sp.get("page") || "1", 10));

const parseOffersListSort = (sp) => {
  const raw = sp.get("sort") || "creation_date";
  return OFFERS_LIST_SORT_FIELDS.includes(raw) ? raw : "creation_date";
};

const parseOffersListOrder = (sp) =>
  sp.get("order") === "asc" ? "asc" : "desc";

const Offers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [isNewOfferOpen, setIsNewOfferOpen] = useState(false);
  const [isOfferDeleteOpen, setIsOfferDeleteOpen] = useState(false);
  const [isOfferEditOpen, setIsOfferEditOpen] = useState(false);
  const [isCreativeDeleteOpen, setIsCreativeDeleteOpen] = useState(false);
  const [operationType, setOperationType] = useState();
  const [isUpdate, setIsUpdate] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [missingCreativeType, setMissingCreativeType] = useState("");
  const [pendingDeleteOfferIds, setPendingDeleteOfferIds] = useState([]);
  const [deleteFromEverflow, setDeleteFromEverflow] = useState(false);
  const [clearSelectionOnDeleteSuccess, setClearSelectionOnDeleteSuccess] =
    useState(false);
  const [deleteNonce, setDeleteNonce] = useState(0);
  const [creativeId, setCreativeId] = useState();
  const [offer, setOffer] = useState();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(() => parseOffersListPage(searchParams));
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(() =>
    parseOffersListSort(searchParams)
  );
  const [sortOrder, setSortOrder] = useState(() =>
    parseOffersListOrder(searchParams)
  );
  const prevSearchRef = useRef(search);
  const prevTagIdsRef = useRef(selectedTagIds);
  const prevMissingCreativeRef = useRef(missingCreativeType);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setPage(parseOffersListPage(searchParams));
    setSortField(parseOffersListSort(searchParams));
    setSortOrder(parseOffersListOrder(searchParams));
  }, [searchParams]);

  const replaceOffersListParams = (updates) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (updates.page !== undefined) {
          params.set("page", String(updates.page));
        }
        if (updates.sort !== undefined) {
          params.set("sort", updates.sort);
        }
        if (updates.order !== undefined) {
          params.set("order", updates.order);
        }
        return params;
      },
      { replace: true }
    );
  };

  const fetchOffers = (
    pageNum = page,
    searchTerm = search,
    sortFieldParam = sortField,
    sortOrderParam = sortOrder,
    tagIds = selectedTagIds,
    missingCreative = missingCreativeType
  ) => {
    setIsLoading(true);
    apiService
      .getAllOffersV2({
        page: pageNum,
        limit: limit,
        sortField: sortFieldParam,
        sortOrder: sortOrderParam,
        search: searchTerm,
        tagIds: tagIds,
        missingCreativeType: missingCreative,
      })
      .then((response) => {
        setLimit(response.data?.pagination?.limit);
        setPage(response.data?.pagination?.page);
        setTotalPages(response.data?.pagination?.totalPages);
        setTotalCount(response.data?.pagination?.total ?? 0);
        setSuggestions(response.data?.pagination?.suggestions || []);
        setOffers(response.data?.data || []);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isUpdate) {
      fetchOffers(
        page,
        search,
        sortField,
        sortOrder,
        selectedTagIds,
        missingCreativeType
      );
      apiService.getAllCreatives().then((response) => {
        setCreatives(response.data);
      });
      prevSearchRef.current = search;
      prevTagIdsRef.current = selectedTagIds;
      prevMissingCreativeRef.current = missingCreativeType;
      setIsUpdate(false);
      return;
    }

    if (prevSearchRef.current !== search) {
      prevSearchRef.current = search;
      setPage(1);
    } else if (prevTagIdsRef.current !== selectedTagIds) {
      prevTagIdsRef.current = selectedTagIds;
      setPage(1);
      fetchOffers(
        1,
        search,
        sortField,
        sortOrder,
        selectedTagIds,
        missingCreativeType
      );
    } else if (prevMissingCreativeRef.current !== missingCreativeType) {
      prevMissingCreativeRef.current = missingCreativeType;
      if (page !== 1) {
        setPage(1);
        replaceOffersListParams({ page: 1 });
      } else {
        fetchOffers(
          1,
          search,
          sortField,
          sortOrder,
          selectedTagIds,
          missingCreativeType
        );
      }
    } else {
      fetchOffers(
        page,
        search,
        sortField,
        sortOrder,
        selectedTagIds,
        missingCreativeType
      );
    }
  }, [
    isUpdate,
    page,
    search,
    sortField,
    sortOrder,
    selectedTagIds,
    missingCreativeType,
  ]);

  const handleOpenNewOffer = (type) => {
    setIsNewOfferOpen(true);
    setOperationType(type);
  };

  const handleOpenOfferEdit = (offer) => {
    setOffer(offer);
    setIsOfferEditOpen(true);
  };

  const handleOpenOfferDelete = (ids, options = {}) => {
    const normalizedIds = Array.isArray(ids) ? ids : [ids];
    const {
      deleteFromEverflow: withEverflow = false,
      clearSelectionOnSuccess = false,
    } = options;
    setPendingDeleteOfferIds(normalizedIds.filter(Boolean));
    setDeleteFromEverflow(withEverflow);
    setClearSelectionOnDeleteSuccess(clearSelectionOnSuccess);
    setIsOfferDeleteOpen(true);
  };

  const handleConfirmOfferDelete = () => {
    if (!pendingDeleteOfferIds?.length) {
      return Promise.resolve();
    }
    return apiService
      .bulkDeleteOffers({
        offer_ids: pendingDeleteOfferIds,
        everflow_included: deleteFromEverflow,
      })
      .then(() => {
        if (clearSelectionOnDeleteSuccess) {
          setDeleteNonce((prev) => prev + 1);
        }
        setIsUpdate(true);
        setIsOfferDeleteOpen(false);
        setPendingDeleteOfferIds([]);
      });
  };

  const handleOpenCreativeDelete = (id) => {
    setCreativeId(id);
    setIsCreativeDeleteOpen(true);
  };

  const handleOfferProblems = (type) => {
    navigate("/offers/offerProblems", {
      state: { type: type },
    });
  };

  const closeModal = () => {
    setIsOfferDeleteOpen(false);
    setIsNewOfferOpen(false);
    setIsOfferEditOpen(false);
    setIsCreativeDeleteOpen(false);
    setPendingDeleteOfferIds([]);
    setDeleteFromEverflow(false);
    setClearSelectionOnDeleteSuccess(false);
    setIsUpdate(true);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    replaceOffersListParams({ page: nextPage });
  };

  const handleSortChange = (field, isAscending) => {
    const apiSortField =
      field === "name" ||
      field === "totalRevenue" ||
      field === "score" ||
      field === "emailScore" ||
      field === "smsScore"
        ? field
        : "creation_date";
    const order = isAscending ? "asc" : "desc";
    setSortField(apiSortField);
    setSortOrder(order);
    replaceOffersListParams({ sort: apiSortField, order });
  };

  const getOfferCountById = (id) => {
    if (!suggestions || suggestions.length === 0) {
      return 0;
    }
    const item = suggestions.find((item) => item._id === id);
    if (!item || item.offerCount === undefined || item.offerCount === null) {
      return 0;
    }
    return item.offerCount;
  };

  return (
    <div className={"Page"}>
      <Header
        handleOpenNewOffer={handleOpenNewOffer}
        setSearch={setSearch}
        search={search}
        setIsUpdate={setIsUpdate}
        selectedTagIds={selectedTagIds}
        setSelectedTagIds={setSelectedTagIds}
        missingCreativeType={missingCreativeType}
        setMissingCreativeType={setMissingCreativeType}
      ></Header>
      {isLoading ? (
        <div className="flex items-center justify-center w-full h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7F56D9]"></div>
            <div
              className="text-[#15151F] text-sm font-medium"
              style={{ fontFamily: "Inter" }}
            >
              Loading...
            </div>
          </div>
        </div>
      ) : (
        <>
          {search && (
            <SearchLine setSearch={setSearch} search={search}></SearchLine>
          )}
          {!search && (
            <div className="flex w-full items-start gap-3 px-6">
              <button
                onClick={() => handleOfferProblems("pending")}
                className="w-full flex py-3 px-2.5 flex-col justify-center items-center gap-1.5 flex-1 rounded-lg border border-[#E5E7F0] bg-white"
                style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
              >
                <div className="w-full flex px-2 items-center gap-2.5 flex-1">
                  <img
                    alt={"Logo"}
                    src={"/media/icons/pending.svg"}
                    className={"w-4 h-4"}
                  />
                  <div className=" w-full flex flex-col items-start gap-[6px] self-stretch">
                    <div
                      className="text-[#15151F] text-xs font-medium leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      Pending Resolution
                    </div>
                    <div
                      className="text-[#15151F] text-2xl font-semibold leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      {getOfferCountById("pending")}
                    </div>
                  </div>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/chevronRight.svg"}
                    className={"w-[18px] h-[18px]"}
                  />
                </div>
              </button>
              <button
                onClick={() => handleOfferProblems("noted")}
                className="w-full flex py-3 px-2.5 flex-col justify-center items-center gap-1.5 flex-1 rounded-lg border border-[#E5E7F0] bg-white"
                style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
              >
                <div className="w-full flex px-2 items-center gap-2.5 flex-1">
                  <img
                    alt={"Logo"}
                    src={"/media/icons/noted.svg"}
                    className={"w-4 h-4"}
                  />
                  <div className=" w-full flex flex-col items-start gap-[6px] self-stretch">
                    <div
                      className="text-[#15151F] text-xs font-medium leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      Noted
                    </div>
                    <div
                      className="text-[#15151F] text-2xl font-semibold leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      {getOfferCountById("noted")}
                    </div>
                  </div>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/chevronRight.svg"}
                    className={"w-[18px] h-[18px]"}
                  />
                </div>
              </button>
              <button
                onClick={() => handleOfferProblems("resolved")}
                className="w-full flex py-3 px-2.5 flex-col justify-center items-center gap-1.5 flex-1 rounded-lg border border-[#E5E7F0] bg-white"
                style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
              >
                <div className="w-full flex px-2 items-center gap-2.5 flex-1">
                  <img
                    alt={"Logo"}
                    src={"/media/icons/resolved.svg"}
                    className={"w-4 h-4"}
                  />
                  <div className=" w-full flex flex-col items-start gap-[6px] self-stretch">
                    <div
                      className="text-[#15151F] text-xs font-medium leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      Resolved
                    </div>
                    <div
                      className="text-[#15151F] text-2xl font-semibold leading-normal"
                      style={{ fontFamily: "Inter" }}
                    >
                      {getOfferCountById("resolved")}
                    </div>
                  </div>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/chevronRight.svg"}
                    className={"w-[18px] h-[18px]"}
                  />
                </div>
              </button>
            </div>
          )}
          {offers?.length > 0 ? (
            <div className={"px-6 w-full h-full"}>
              <OffersTable
                handleOpenOfferEdit={handleOpenOfferEdit}
                closeModal={closeModal}
                handleOpenOfferDelete={handleOpenOfferDelete}
                onRequestDeleteOffers={handleOpenOfferDelete}
                deleteNonce={deleteNonce}
                list={offers}
                setList={setOffers}
                handleOpenCreativeDelete={handleOpenCreativeDelete}
                creatives={creatives}
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={limit}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
                sortField={sortField}
                sortOrder={sortOrder}
              />
            </div>
          ) : (
            <Empty
              handleNew={handleOpenNewOffer}
              title={
                missingCreativeType ? "No Offers Found" : "No Offers Available"
              }
              description={
                missingCreativeType === "SMS"
                  ? "No active offers are missing an SMS creative."
                  : missingCreativeType === "Email"
                  ? "No active offers are missing an email creative."
                  : "Create new offers to see them ranked here."
              }
              type={"Offer"}
              menu={PageEnum.OFFERS}
              noButton={Boolean(missingCreativeType)}
            ></Empty>
          )}
        </>
      )}

      {isNewOfferOpen && (
        <NewOffer
          closeModal={closeModal}
          setIsNewOfferOpen={setIsNewOfferOpen}
          type={operationType}
        />
      )}
      {isOfferEditOpen && (
        <NewOffer
          closeModal={closeModal}
          type={OperationType.UPDATE}
          offer={offer}
        />
      )}
      {isOfferDeleteOpen && (
        <OfferDelete
          closeModal={closeModal}
          setIsOfferDeleteOpen={setIsOfferDeleteOpen}
          offerCount={pendingDeleteOfferIds?.length}
          deleteFromEverflow={deleteFromEverflow}
          onConfirmDelete={handleConfirmOfferDelete}
        />
      )}
      {isCreativeDeleteOpen && (
        <CreativeDelete
          closeModal={closeModal}
          setIsCreativeDeleteOpen={setIsCreativeDeleteOpen}
          creativeId={creativeId}
        />
      )}
    </div>
  );
};

export default injectIntl(Offers);
